import { put, select, takeLatest } from 'redux-saga/effects';

import { ApiStatusCodes } from '../../../services/api/apiStatusCode';
import { agencyAllocatorConfigEndpoints } from '../../../services/api/endPoints';
import { HTTPClient } from '../../../services/api/http-client';
import {
  AGENCY_ALLOCATOR_CONFIG_SESSION_KEY,
  fetchAgencyAllocatorConfig,
  fetchAgencyAllocatorConfigFailure,
  fetchAgencyAllocatorConfigSuccess,
  resetAgencyAllocatorConfig,
} from '../../slice/agencyAllocatorConfig/AgencyAllocatorConfig.slice';
import { updateLenderIdInUserConfiguration } from '../../slice/user/UserConfiguration.slice';
import { RootState } from '../../store/store';

interface AgencyAllocatorConfigResponse {
  agencyId?: string;
  allocatorId?: string;
  config?: {
    PACING_RATIO?: string;
    DIAL_PAD?: string;
  };
}

export function* fetchAgencyAllocatorConfigSaga() {
  try {
    const agencyId: string = yield select(
      (state: RootState) => state.userConfiguration.agencyId || ''
    );
    const lenderId: string = yield select(
      (state: RootState) => state.userConfiguration.lenderId || ''
    );

    if (!agencyId || !lenderId) {
      yield put(
        fetchAgencyAllocatorConfigFailure(
          'Missing agencyId or allocatorId for config fetch.'
        )
      );
      return;
    }

    const result: { data: AgencyAllocatorConfigResponse; status: number } =
      yield HTTPClient.GetRequest<AgencyAllocatorConfigResponse>(
        agencyAllocatorConfigEndpoints.AGENCY_ALLOCATOR_CONFIG
      );

    const { data, status } = result;

    if (status === ApiStatusCodes.SUCCESS && data) {
      const pacingRatio =
        typeof data.config?.PACING_RATIO === 'string'
          ? data.config.PACING_RATIO
          : undefined;
      const dialPad = data.config?.DIAL_PAD === 'true';

      const payload = { pacingRatio, dialPad };

      try {
        sessionStorage.setItem(
          AGENCY_ALLOCATOR_CONFIG_SESSION_KEY,
          JSON.stringify(payload)
        );
      } catch {
        // sessionStorage unavailable (private mode / quota) — proceed without persistence
      }

      yield put(fetchAgencyAllocatorConfigSuccess(payload));
    } else {
      throw new Error('Unexpected status while fetching allocator config');
    }
  } catch (error) {
    yield put(
      fetchAgencyAllocatorConfigFailure(
        'Failed to fetch agency allocator config.'
      )
    );
  }
}

export function* onAllocatorSelectedSaga() {
  try {
    sessionStorage.removeItem(AGENCY_ALLOCATOR_CONFIG_SESSION_KEY);
  } catch {
    // ignore
  }
  yield put(resetAgencyAllocatorConfig());
  yield put(fetchAgencyAllocatorConfig());
}

export function* watchAgencyAllocatorConfigSaga() {
  yield takeLatest(
    fetchAgencyAllocatorConfig.type,
    fetchAgencyAllocatorConfigSaga
  );
  yield takeLatest(
    updateLenderIdInUserConfiguration.type,
    onAllocatorSelectedSaga
  );
}
